import React, { useEffect, useMemo, useState } from 'react';
import ImageCarousel from '../common/ImageCarousel';
import { OptimizedImage } from '../common/OptimizedImage';
import InclusionBadge from '../common/InclusionBadge';
import InteractivePopover from '../common/InteractivePopover';
import CurrencyDisplay from '../common/CurrencyDisplay';

interface IconProps {
  images?: string[]; // optional list of images for slider
  src?: string; // backward compatibility: single image
  alt?: string;
  label: string;
  isIncluded?: boolean;
  description?: string; // Tooltip description
  // Additional activity details
  startTime?: string;
  endTime?: string;
  duration?: number; // Duration in minutes
  price?: number; // payAtSiteCost
  currency?: string;
  currencySymbol?: string;
  location?: {
    name?: string;
    city?: string;
    state?: string;
  };
  enableFullscreen?: boolean;
}

const Icon: React.FC<IconProps> = ({
  images,
  src,
  alt = '',
  label,
  isIncluded,
  description,
  startTime,
  endTime,
  duration,
  price,
  currency,
  enableFullscreen = true,
}) => {
  const imageList = useMemo(() => {
    // Filter out empty, null, or undefined strings
    const validImages = (
      images && images.length > 0 ? images : src ? [src] : []
    ).filter(img => img && typeof img === 'string' && img.trim().length > 0);
    return validImages.length > 0
      ? validImages
      : ['/assets/images/No-Image.jpg'];
  }, [images, src]);

  // Generate a safe navigation ID
  const navigationId = useMemo(() => {
    const safeName = (label || 'icon').replace(/[^a-zA-Z0-9-_]/g, '-');
    return `${safeName}-${Math.random().toString(36).substr(2, 9)}`;
  }, [label]);

  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [shouldShowSeeMore, setShouldShowSeeMore] = useState(false);
  const descriptionRef = React.useRef<HTMLParagraphElement | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia('(max-width: 768px)');
    const updateIsMobile = (event: MediaQueryListEvent | MediaQueryList) => {
      setIsMobile(event.matches);
    };

    updateIsMobile(mediaQuery);
    mediaQuery.addEventListener('change', updateIsMobile);

    return () => {
      mediaQuery.removeEventListener('change', updateIsMobile);
    };
  }, []);

  // Check if description actually overflows when clamped
  const checkDescriptionOverflow = (element: HTMLParagraphElement) => {
    if (!element) return;

    // Create a temporary element to measure natural height
    const tempElement = element.cloneNode(true) as HTMLParagraphElement;
    tempElement.className = element.className
      .replace('line-clamp-3', '')
      .trim();
    tempElement.style.visibility = 'hidden';
    tempElement.style.position = 'absolute';
    tempElement.style.width = element.offsetWidth + 'px';
    tempElement.style.top = '-9999px';
    document.body.appendChild(tempElement);

    const naturalHeight = tempElement.scrollHeight;
    document.body.removeChild(tempElement);

    // Calculate expected height for 3 lines (line-height: 17px * 3 = 51px)
    // Add a small buffer for rounding
    const expectedClampedHeight = 17 * 3 + 2;

    // If natural height exceeds expected clamped height, show "see more"
    const hasOverflow = naturalHeight > expectedClampedHeight;
    setShouldShowSeeMore(hasOverflow);
  };

  const imageSection = (
    <div>
      <ImageCarousel
        images={imageList}
        alt={alt || label}
        className='w-full md:h-[4.5rem] h-24 mb-3'
        imageClassName='rounded-md object-cover'
        showNavigation={true}
        navigationId={`icon-${navigationId}`}
        buttonSize='sm'
        loop={false}
        enableFullscreen={enableFullscreen}
        fullscreenTitle={label}
      />
    </div>
  );

  const textSection = (
    <div className='flex flex-col justify-between'>
      <span className='text-[12px] text-left leading-5 capitalize truncate block'>
        {label}
      </span>
      <div className='flex flex-col'>
        {isIncluded !== undefined && (
          <InclusionBadge
            isIncluded={isIncluded}
            variant='short'
            size='sm'
            className='mt-3'
            includedText='Complimentary Access'
            notIncludedText='Optional'
          />
        )}
      </div>
    </div>
  );

  const content = (
    <div className='flex flex-col justify-between'>
      {imageSection}
      {textSection}
    </div>
  );

  const detailedCard = (
    <div className='bg-white rounded-[15px] border border-white shadow-[0px_0px_5px_1px_rgba(0,0,0,0.15)] max-h-[500px] overflow-hidden flex flex-col'>
      {/* Image Carousel */}
      <div className='relative overflow-hidden rounded-t-[15px]'>
        {imageList.length > 0 &&
        imageList[0] !== '/assets/images/No-Image.jpg' ? (
          <ImageCarousel
            images={imageList}
            alt={label}
            className='w-full h-[120px]'
            imageClassName='object-cover'
            showNavigation={false}
            showPagination={true}
            autoplay={true}
            loop={false}
            navigationId={`popup-${navigationId}`}
            buttonSize='sm'
          />
        ) : (
          <div className='w-full h-[120px] relative overflow-hidden'>
            <OptimizedImage
              src='/assets/images/No-Image.jpg'
              alt={label}
              fill
              style={{ objectFit: 'cover' }}
              priority
              loading='eager'
            />
          </div>
        )}
      </div>

      {/* Content */}
      <div className='p-3 overflow-y-auto flex-1'>
        {/* Title */}
        <h3 className='text-[14px] font-semibold text-black leading-[17px] mb-2 capitalize'>
          {label}
        </h3>

        {/* Description */}
        {description && (
          <div className='mt-2'>
            <p
              ref={el => {
                descriptionRef.current = el;
                // Check overflow after element is rendered
                if (el) {
                  // Use requestAnimationFrame to ensure DOM is updated
                  requestAnimationFrame(() => {
                    checkDescriptionOverflow(el);
                  });
                }
              }}
              className={`text-[12px] font-normal text-darkgrey leading-[17px] ${
                !isDescriptionExpanded ? 'line-clamp-3' : ''
              }`}
            >
              {description}
            </p>
            {shouldShowSeeMore && (
              <button
                onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
                className='text-black font-medium mt-1 text-[11px] underline'
              >
                {isDescriptionExpanded ? 'See less' : 'See more'}
              </button>
            )}
          </div>
        )}

        {/* Time & Price Section */}
        {(startTime ||
          endTime ||
          duration ||
          (price !== undefined && price > 0)) && (
          <div className='mt-2.5 pt-2.5 border-t border-gray-200'>
            <div className='flex justify-between gap-4'>
              {/* Time & Duration */}
              {(startTime || endTime || duration) && (
                <div className='flex-1'>
                  {startTime && endTime ? (
                    <>
                      <p className='text-[12px] font-medium text-black mb-1'>
                        Time:
                      </p>
                      <span className='text-[12px] text-darkgrey'>
                        {startTime} - {endTime}
                      </span>
                    </>
                  ) : duration ? (
                    <>
                      <p className='text-[12px] font-medium text-black mb-1'>
                        Duration:
                      </p>
                      <span className='text-[12px] text-darkgrey'>
                        {duration >= 60
                          ? `${Math.floor(duration / 60)}h ${duration % 60 > 0 ? `${duration % 60}m` : ''}`
                          : `${duration}m`}
                      </span>
                    </>
                  ) : null}
                </div>
              )}

              {/* Price */}
              {price !== undefined && price > 0 && (
                <div className='flex-1'>
                  <p className='text-[12px] font-medium text-black mb-1'>
                    Price:
                  </p>
                  <div className='flex items-baseline gap-0.5'>
                    <CurrencyDisplay
                      amount={price}
                      backendCurrency={currency}
                      fontSize='14px'
                      className='font-semibold text-black'
                      showDecimals={false}
                    />
                    <span className='text-[12px] font-normal text-darkgrey'>
                      /night
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Included/Not Included Section */}
        {isIncluded !== undefined && (
          <div className='mt-2.5 pt-2.5 border-t border-gray-100'>
            <InclusionBadge
              isIncluded={isIncluded}
              variant='long'
              size='sm'
              includedText='Complimentary Access'
              notIncludedText='chargeable at the site'
            />
          </div>
        )}
      </div>
    </div>
  );

  const triggerWrapperClasses =
    'cursor-pointer hover:opacity-90 transition-opacity';

  // If description exists, wrap in InteractivePopover
  if (description) {
    return (
      <InteractivePopover
        triggerClassName={`${triggerWrapperClasses} flex flex-col`}
        contentClassName={
          isMobile
            ? 'w-full max-w-[min(22rem,calc(100%-2rem))] p-0 !rounded-[15px] !border-white border shadow-[0px_0px_5px_1px_rgba(0,0,0,0.15)] overflow-hidden'
            : 'w-[320px] p-0 !rounded-[15px] !border-white border shadow-[0px_0px_5px_1px_rgba(0,0,0,0.15)] overflow-hidden'
        }
        align='start'
        sideOffset={5}
        collisionPadding={20}
        trigger={
          <>
            {imageSection}
            {textSection}
          </>
        }
        content={detailedCard}
        onOpenChange={() => setIsDescriptionExpanded(false)}
      />
    );
  }

  return content;
};

export default Icon;
